import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, Dialog, Field, Input, Select, toast } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Eye, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type ShopifyCollection, type ShopifyCollectionDTO } from "../../api";
import { ExternalLink, formatDate, formatDateTime } from "../../components/cells";
import { crudEditor } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Shopify collections, reconciled across every storefront.
 *
 * The list is Shopify's, not ours: a collection created in the Shopify admin
 * appears here, and the local mapping row exists only so the manual ordering
 * this app applies has somewhere to live. That reconciliation is why the page
 * reads both sides and shows the live title and product count.
 */
export function ShopifyCollectionsPage() {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.COLLECTION_SHOPIFY);
  const title = t("navigation.item.shopify-collections", "Shopify");
  const [creating, setCreating] = useState(false);

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: allowed,
  });

  const live = useQuery({
    queryKey: ["shopify-collection", "live"],
    queryFn: () => api.shopifyCollections.getCollectionsAll(),
    enabled: allowed,
  });

  /**
   * Mapping rows, reconciled against Shopify on every load.
   *
   * Shopify is the source of truth for which collections exist; the local row
   * only exists so this app's manual ordering has an id to hang off. So a
   * collection created in the Shopify admin gets a row here, and a collection
   * deleted there loses one — the same reconciliation `ShopifyCollectionView`
   * ran before it painted its grid.
   */
  const mappings = useQuery({
    queryKey: ["shopify-collection", "mappings"],
    queryFn: async () => {
      const [existing, storeList, byStore] = await Promise.all([
        api.shopifyCollections.collectionsAll(),
        api.stores.findAll(),
        api.shopifyCollections.getCollectionsAll(),
      ]);

      const storeById = new Map(storeList.map((entry) => [entry.id, entry]));
      const result: ShopifyCollectionDTO[] = [];

      for (const [storeKey, collections] of Object.entries(byStore)) {
        const currentStoreId = Number(storeKey);
        const store = storeById.get(currentStoreId);
        if (!store) continue;

        const rowsForStore = existing.filter((row) => row.store?.id === currentStoreId);

        for (const collection of collections) {
          const matched = rowsForStore.find((row) => row.collection_id === collection.id);
          if (matched) {
            result.push(matched);
          } else {
            const created = await api.shopifyCollections.createMapping(currentStoreId, {
              collection_id: collection.id,
              store,
              rules: [],
            });
            result.push(created);
          }
        }

        for (const row of rowsForStore) {
          if (!collections.some((collection) => collection.id === row.collection_id)) {
            await api.shopifyCollections.removeMapping(currentStoreId, row.id);
          }
        }
      }

      return result;
    },
    enabled: allowed && live.isSuccess,
  });

  /** Shopify collection id → its live payload, across every store. */
  const liveById = useMemo(() => {
    const map = new Map<string, ShopifyCollection>();
    for (const list of Object.values(live.data ?? {})) {
      for (const collection of list) map.set(collection.id, collection);
    }
    return map;
  }, [live.data]);

  const rows = mappings.data ?? [];

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["shopify-collection"] });
  };

  const cols = useMemo(() => {
    const c = defineColumns<ShopifyCollectionDTO>();
    return [
      c.text("store", (row) => row.store?.name ?? "", {
        headerKey: "column.store",
        filter: "select",
        priority: 2,
      }),
      c.text("title", (row) => liveById.get(row.collection_id)?.title ?? "", {
        headerKey: "column.title",
        priority: 1,
      }),
      c.number(
        "product_count",
        (row) => liveById.get(row.collection_id)?.productsCount?.count ?? 0,
        { headerKey: "column.product-count", priority: 3 },
      ),
      c.custom(
        "url",
        (row) => liveById.get(row.collection_id)?.handle ?? "",
        (row) => {
          const handle = liveById.get(row.collection_id)?.handle;
          const base = row.store?.url ?? "";
          return handle && base ? (
            <ExternalLink href={`${base}collections/${handle}`}>
              {t("value.url", "Link")}
            </ExternalLink>
          ) : (
            <span className="text-ink-faint">—</span>
          );
        },
        { headerKey: "column.url", filter: "none", enableSorting: false, priority: 4 },
      ),
      c.custom(
        "updated_at",
        // formatDateTime's long, locale-written string isn't lexicographically
        // sortable or comparable as a date; formatDate's plain ISO day (unused
        // for display here) backs sorting and the daterange filter instead.
        (row) => formatDate(liveById.get(row.collection_id)?.updatedAt),
        (row) => formatDateTime(liveById.get(row.collection_id)?.updatedAt),
        { headerKey: "column.updated-at", filter: "daterange", priority: 4 },
      ),
      c.custom(
        "rules",
        (row) => (liveById.get(row.collection_id)?.ruleSet ? "auto" : "manual"),
        (row) => (
          <span className="text-ink-muted">
            {liveById.get(row.collection_id)?.ruleSet ? "automated" : "manual"}
          </span>
        ),
        { headerKey: "column.rules", filter: "select", priority: 5 },
      ),
    ];
  }, [liveById, t]);

  /** The title is Shopify's, so renaming a row pushes the change upstream. */
  const editor = useMemo(
    () =>
      crudEditor<ShopifyCollectionDTO>({
        client: api.shopifyCollections,
        refresh,
        t,
        canDelete: false,
        fields: {
          title: {
            kind: "text",
            get: (row) => liveById.get(row.collection_id)?.title ?? "",
            set: (row, value) => ({ ...row, __title: value }) as ShopifyCollectionDTO,
            required: true,
          },
        },
        onSave: async (row) => {
          const nextTitle = (row as ShopifyCollectionDTO & { __title?: string }).__title;
          if (!nextTitle) return;
          await api.shopifyCollections.updateTitle(row.store.id, row.id, nextTitle);
        },
      }),
    [api, liveById, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (mappings.error || live.error)
    return <ErrorState title={title} error={mappings.error ?? live.error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<ShopifyCollectionDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        stateKey="/collection/shopify-collections"
        loading={mappings.isLoading || live.isLoading}
        editor={editor}
        actions={[
          {
            label: t("dialog.add-collection.title", "Add collection"),
            icon: <Plus className="size-4" />,
            variant: "primary",
            write: true,
            onClick: () => setCreating(true),
          },
        ]}
        onRowClick={(row) =>
          void navigate({
            to: "/collection/shopify-collections/$id",
            params: { id: String(row.id) },
          })
        }
        rowActions={(row) => (
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("tab.products", "Products")}
            icon={<Eye className="size-3.5" />}
            onClick={() =>
              void navigate({
                to: "/collection/shopify-collections/$id",
                params: { id: String(row.id) },
              })
            }
          />
        )}
      />

      <AddCollectionDialog
        open={creating}
        onClose={() => setCreating(false)}
        stores={(stores.data ?? []).map((entry) => ({ id: entry.id, name: entry.name }))}
        onCreated={() => void refresh()}
      />
    </>
  );
}

function AddCollectionDialog({
  open,
  onClose,
  stores,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  stores: Array<{ id: number; name: string }>;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [storeId, setStoreId] = useState("");
  const [collectionTitle, setCollectionTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!storeId || !collectionTitle.trim()) return;
    setBusy(true);
    try {
      // Reject a duplicate title before hitting Shopify, which would otherwise
      // happily create a second collection with the same name.
      const existing = await api.shopifyCollections.getCollections(Number(storeId));
      if (existing.some((entry) => entry.title?.toLowerCase() === collectionTitle.toLowerCase())) {
        toast.error(t("form.validation.field.unique", "Value already exists"));
        return;
      }
      await api.shopifyCollections.createRemote(Number(storeId), collectionTitle);
      toast.success(t("notification.add-collection.success", "Collection added successfully"));
      setCollectionTitle("");
      onCreated();
      onClose();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.add-collection.error", "Failed to add collection"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("dialog.add-collection.title", "Add collection")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!storeId || !collectionTitle.trim()}
            onClick={() => void submit()}
          >
            {t("dialog.confirm.add", "Add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("form.field.store", "Store")} required>
          <Select
            value={storeId}
            options={stores.map((entry) => ({ value: String(entry.id), label: entry.name }))}
            placeholder={t("form.field.select", "Select")}
            onChange={(event) => setStoreId(event.target.value)}
          />
        </Field>
        <Field label={t("form.field.title", "Title")} required>
          <Input
            value={collectionTitle}
            onChange={(event) => setCollectionTitle(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
